/**
 * The spoken reminder for the moment it is time to leave.
 *
 * There was a synthesised bell in front of this once. It was dropped: iOS hands the audio
 * session to whoever asked last, so the bell would play and the speech that followed would be
 * discarded without a sound or an error. The push notification already carries the system
 * alert sound, which makes a second attention-getter redundant anyway.
 *
 * Only usable while the app is open. The notification covers everything else.
 */

import { leaveWindowText, trainAlertText } from "../core/alerts";
import { noteAlertDiagnostics, noteSpokenAlert } from "./alertDiagnostics";
import { report } from "./report";

let activeUtterance: SpeechSynthesisUtterance | null = null;
let voicePrimed = false;
let lastPrimeAtMs = 0;
let lastGestureAtMs = 0;
/** Whether a priming utterance was ever heard to start, as opposed to merely accepted. */
let primeStarted = false;

/** How long an unlock is assumed to last before it is worth spending another one. */
const PRIME_STALE_MS = 25_000;

/** Called from a real tap, so the gap between a gesture and an utterance can be measured. */
export function noteGesture(): void {
	lastGestureAtMs = Date.now();
}

/**
 * Unlock speech while a gesture is still in scope.
 *
 * Speech is gesture-gated on iOS: an utterance started later by a timer or a push is dropped
 * in silence unless the queue has been used recently. This used to run once and never again,
 * which meant a seven o'clock reminder was relying on an unlock from the night before.
 */
export function primeVoice(): void {
	if (!("speechSynthesis" in window) || !("SpeechSynthesisUtterance" in window)) {
		return;
	}
	if (voicePrimed && Date.now() - lastPrimeAtMs < PRIME_STALE_MS) {
		return;
	}
	try {
		const silent = new SpeechSynthesisUtterance(" ");
		// Not zero. Every record ever written says the priming utterance never started, which is
		// iOS discarding silent ones outright — so the gesture that was meant to unlock the engine
		// never exercised it. A single space at a whisper is inaudible but real.
		silent.volume = 0.05;
		// If this never fires, the unlock was never real and nothing after it will be spoken.
		silent.onstart = () => {
			primeStarted = true;
		};
		window.speechSynthesis.speak(silent);
		lastPrimeAtMs = Date.now();
		voicePrimed = true;
		noteAlertDiagnostics({ voicePrimed: true });
	} catch (cause) {
		noteAlertDiagnostics({ speechError: cause instanceof Error ? cause.message : "priming refused" });
	}
}

export function playLeaveAlert(voiceURI: string | null = null, minutesBefore = 0, nowMs = Date.now()): void {
	const voice = pickVoice(voiceURI);
	speak(leaveAlertPhrase(minutesBefore, nowMs), voice, minutesBefore, "leave", isBritish(voice));
}

export function playTrainAlert(voiceURI: string | null = null, minutesBefore = 5): void {
	const voice = pickVoice(voiceURI);
	speak(trainAlertPhrase(minutesBefore, isBritish(voice)), voice, minutesBefore, "train");
}

function pickVoice(voiceURI: string | null): SpeechSynthesisVoice | null {
	if (!("speechSynthesis" in window)) {
		return null;
	}
	return window.speechSynthesis.getVoices().find((candidate) => candidate.voiceURI === voiceURI) ?? null;
}

function isBritish(voice: SpeechSynthesisVoice | null): boolean {
	return voice?.lang?.toLowerCase().startsWith("en-gb") ?? false;
}

/**
 * Its own utterance, because Safari ignores SSML and prosody only applies to a whole one.
 * Slowing it stretches the rising "-o", which is the nearest thing to emphasis available.
 */
export const SIGN_OFF = "Cheerio!";

function speak(
	text: string,
	voice: SpeechSynthesisVoice | null,
	minutesBefore: number,
	category: "leave" | "train",
	signOff = false,
	attempt = 1,
): void {
	if (!("speechSynthesis" in window) || !("SpeechSynthesisUtterance" in window)) {
		noteAlertDiagnostics({ speechError: "speech synthesis unavailable" });
		noteSpokenAlert({ minutesBefore, category, atMs: Date.now(), status: "failed" });
		return;
	}

	// iOS can leave the queue paused after the app has been in the background, and a paused
	// queue swallows everything handed to it afterwards.
	try {
		window.speechSynthesis.resume();
	} catch {
		// Nothing to resume.
	}

	const engine = window.speechSynthesis;
	const queue = {
		speaking: engine.speaking,
		pending: engine.pending,
		paused: engine.paused,
		voices: engine.getVoices().length,
		atMs: Date.now(),
	};
	noteAlertDiagnostics({ speechQueue: queue });
	report("speak", {
		...queue,
		attempt,
		category,
		minutesBefore,
		primed: voicePrimed,
		primeStarted,
		// The test button works and the timer does not, so this is the number that matters.
		sinceGestureMs: lastGestureAtMs === 0 ? null : Date.now() - lastGestureAtMs,
		sincePrimeMs: lastPrimeAtMs === 0 ? null : Date.now() - lastPrimeAtMs,
		visibility: document.visibilityState,
		voice: voice?.voiceURI ?? "default",
	});
	// A backlog with nothing playing is a stuck queue, and everything handed to it afterwards
	// waits behind it forever. Speech already in progress is left alone: cancelling that was
	// killing utterances a moment after they started.
	if (engine.pending && !engine.speaking) {
		try {
			engine.cancel();
		} catch {
			// Nothing to clear.
		}
	}
	// After the clearing, so the unlock is not the thing being cleared.
	primeVoice();

	activeUtterance = new SpeechSynthesisUtterance(text);
	activeUtterance.voice = voice;
	activeUtterance.rate = 0.95;
	// "Queued" and "said out loud" are different things on iOS, and the gap between them is
	// exactly where a silent failure hides.
	activeUtterance.onstart = () => {
		noteSpokenAlert({ minutesBefore, category, atMs: Date.now(), status: "spoken" });
		report("started", { category, minutesBefore, visibility: document.visibilityState });
	};
	activeUtterance.onend = () => {
		activeUtterance = null;
		noteSpokenAlert({ minutesBefore, category, atMs: Date.now(), status: "spoken" });
	};
	activeUtterance.onerror = (event) => {
		activeUtterance = null;
		noteAlertDiagnostics({ speechError: event.error ?? "speech error" });
		noteSpokenAlert({ minutesBefore, category, atMs: Date.now(), status: "failed" });
		report("failed", { category, minutesBefore, error: event.error ?? null });
	};

	try {
		noteSpokenAlert({ minutesBefore, category, atMs: Date.now(), status: "queued" });
		noteAlertDiagnostics({ speechError: null });
		window.speechSynthesis.speak(activeUtterance);
		if (signOff) {
			const tail = new SpeechSynthesisUtterance(SIGN_OFF);
			tail.voice = voice;
			tail.rate = 0.8;
			tail.pitch = 1.2;
			window.speechSynthesis.speak(tail);
		}
		// A voice that getVoices() lists is not necessarily one iOS will speak with: the compact
		// variants in particular are accepted and then silently ignored. The retry drops it.
		watchForSilence(attempt === 1 ? () => speak(text, null, minutesBefore, category, false, 2) : null, voice?.voiceURI ?? "default", minutesBefore, category);
	} catch (cause) {
		noteAlertDiagnostics({ speechError: cause instanceof Error ? cause.message : "speak refused" });
		noteSpokenAlert({ minutesBefore, category, atMs: Date.now(), status: "failed" });
	}
}

/**
 * An utterance that never starts reports nothing at all, so the only way to notice is to look
 * again shortly afterwards. One retry: if a clean queue will not start it either, something is
 * wrong that trying harder will not fix.
 */
function watchForSilence(retry: (() => void) | null, voiceURI: string, minutesBefore: number, category: "leave" | "train"): void {
	const started = activeUtterance;
	window.setTimeout(() => {
		if (activeUtterance !== started || !("speechSynthesis" in window)) {
			return;
		}
		if (window.speechSynthesis.speaking) {
			return;
		}
		noteAlertDiagnostics({ speechError: retry ? "never started, retrying" : "never started" });
		noteSpokenAlert({ minutesBefore, category, atMs: Date.now(), status: "dropped" });
		report("never-started", {
			retrying: retry != null,
			voice: voiceURI,
			primeStarted,
			sinceGestureMs: lastGestureAtMs === 0 ? null : Date.now() - lastGestureAtMs,
			visibility: document.visibilityState,
			paused: window.speechSynthesis.paused,
		});
		if (retry) {
			try {
				window.speechSynthesis.cancel();
			} catch {
				// Nothing to clear.
			}
			retry();
		}
	}, 2000);
}

/** What the reminder actually says, greeting and all. Exported so it can be checked. */
export function leaveAlertPhrase(minutesBefore: number, nowMs: number): string {
	const hour = new Date(nowMs).getHours();
	const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
	return `${greeting}, ${leaveWindowText(minutesBefore)}.`;
}

/**
 * No greeting here: by now he has been greeted once and is on a platform, so the reminder is
 * all business.
 */
export function trainAlertPhrase(minutesBefore: number, british: boolean): string {
	const opener = british ? (minutesBefore <= 1 ? "Chop chop" : "Look lively") : "Heads up";
	return `${opener}, ${trainAlertText(minutesBefore)}.`;
}
